import React from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Calendar, MapPin, Ticket } from 'lucide-react';
import { Badge } from './ui/badge';

const EventCard = ({ event, featured = false }) => {
  const eventDate = new Date(event.date);
  const minPrice = Math.min(...(event.ticket_types || []).map(t => t.price || 0));
  const isFree = event.is_free || minPrice === 0;

  const defaultImages = {
    music: 'https://images.unsplash.com/photo-1567105800714-0106cfd63b55?w=800&q=80',
    sports: 'https://images.unsplash.com/photo-1552674605-db6ffd4facb5?w=800&q=80',
    arts: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=80',
    nightlife: 'https://images.unsplash.com/photo-1657054711964-a0eb014c2df1?w=800&q=80',
    conference: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&q=80',
    food: 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=800&q=80',
    family: 'https://images.unsplash.com/photo-1511895426328-dc8714191300?w=800&q=80',
    other: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=800&q=80'
  };

  const imageUrl = event.image_url || defaultImages[event.category] || defaultImages.other;

  return (
    <Link
      to={`/events/${event.event_id}`}
      className={`event-card group block ${featured ? 'aspect-[16/10]' : 'aspect-[4/5]'}`}
      data-testid={`event-card-${event.event_id}`}
    >
      <div className="absolute inset-0">
        <img
          src={imageUrl}
          alt={event.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          onError={(e) => {
            e.target.src = defaultImages.other;
          }}
        />
      </div>
      
      <div className="absolute inset-0 z-20 p-4 flex flex-col justify-between">
        {/* Top badges */}
        <div className="flex justify-between items-start">
          <Badge variant="secondary" className="bg-white/90 text-secondary-foreground dark:bg-card/90">
            {event.category}
          </Badge>
          {isFree ? (
            <Badge className="bg-green-500 text-white">
              GRATIS
            </Badge>
          ) : (
            <Badge className="bg-primary text-primary-foreground">
              Desde L.{minPrice.toFixed(2)}
            </Badge>
          )}
        </div>

        {/* Bottom content */}
        <div className="space-y-2">
          <h3 className={`font-bold text-white ${featured ? 'text-2xl md:text-3xl' : 'text-lg'} line-clamp-2`}>
            {event.title}
          </h3>
          
          <div className="flex flex-wrap gap-3 text-sm text-white/90">
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span>{format(eventDate, "d 'de' MMM, yyyy", { locale: es })}</span>
            </div>
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              <span className="truncate max-w-[150px]">{event.location}</span>
            </div>
          </div>

          {/* Available tickets */}
          <div className="flex items-center gap-2 text-xs text-white/70">
            <Ticket className="w-3 h-3" />
            <span>{event.total_tickets - event.sold_tickets} tickets disponibles</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default EventCard;
